import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Spinner } from 'common/spinner';
import { NewEntryModal } from 'common/new-entry-modal';
import { UploadModal } from 'common/upload-modal';
import {
  AlbumAddContentTypes,
  AlbumContentType,
  Breakpoints,
  VaultNavigationTabs,
} from 'common/enums';
import { appActions, AppContext } from 'context/app-context';
import { SelfRecordingContext } from 'context/self-recording-context';
import {
  VaultDetailContext,
  VaultDetailProvider,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { withAuth } from 'hocs/auth/with-auth';
import { hasAlbumOrder } from 'helpers/memory-order';
import { adaptContentType, albumDisplayName, classnames } from 'helpers/utils';
import { VaultsController } from 'networking/controllers/vaults-controller';
import {
  AlbumContentSerialized,
  AlbumDetailType,
} from 'networking/types/album-detail';
import { goToPage, RouteName } from 'routes';
import { albumPageBreadcrumb, withOrigin } from 'helpers/vault-breadcrumbs';
import { useBreadcrumbOrigin } from 'hooks/use-breadcrumb-origin';
import { useMediaQuery } from 'hooks/use-media-query';
import { VaultTopBar } from 'pages/vault-detail/components/vault-top-bar';
import { AlbumNavRow } from './components/album-nav-row';
import { AlbumHeader } from './components/album-header';
import { AlbumPhoneHeader } from './components/album-phone-header';
import { MemoryGrid } from './components/memory-grid';
import { AlbumPlanner } from './components/album-planner';
import {
  AlbumSortKey,
  AlbumViewKind,
  isPromptContent,
  sortAlbumContents,
} from './single-album-utils';
import styles from './single-album.module.scss';

// Duration of the leave/enter halves of the grid<->planner transition.
// Keep in sync with the animation timings in single-album.module.scss.
const VIEW_LEAVE_MS = 240;
const VIEW_ENTER_MS = 520;

const SingleAlbumContent = () => {
  const { id: urlVaultId, albumId: urlAlbumId } = useParams<{
    id: string;
    albumId: string;
  }>();
  const vaultId = Number(urlVaultId);
  const albumId = Number(urlAlbumId);
  const location = useLocation();
  // Where the user came from (dashboard vs Albums tab), for the breadcrumb.
  const breadcrumbOrigin = useBreadcrumbOrigin();

  const { state: appState, dispatch } = useContext(AppContext);
  const { state: selfRecordingState } = useContext(SelfRecordingContext);
  const {
    state: { searchQuery, refreshToken },
    dispatch: dispatchVaultDetail,
  } = useContext(VaultDetailContext);

  const [album, setAlbum] = useState<AlbumDetailType | null>(null);
  const [fetching, setFetching] = useState(true);
  const [sortBy, setSortBy] = useState<AlbumSortKey>('oldest');
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [addEntryInitialType, setAddEntryInitialType] =
    useState<AlbumAddContentTypes | null>(null);

  // View toggle with a two-phase (leave -> enter) transition.
  const [view, setView] = useState<AlbumViewKind>('grid');
  const [displayedView, setDisplayedView] = useState<AlbumViewKind>('grid');
  const [viewPhase, setViewPhase] = useState<'idle' | 'leaving' | 'entering'>(
    'idle',
  );
  const isPhone = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);

  const vault = appState.currentVault;

  // The album comes from its own endpoint; the vault is fetched in summary
  // form (albums without contents) for the top bar, breadcrumb and the album
  // list in the move/copy dialogs. Loading the whole vault with every memory
  // made a six-video album as slow as the biggest vault's dashboard.
  const fetchAlbum = async (silent = false) => {
    try {
      if (!silent) setFetching(true);
      const [fetchedAlbum, fetchedVault] = await Promise.all([
        VaultsController.getAlbum(albumId),
        VaultsController.getVault(vaultId, 'summary'),
      ]);
      dispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: fetchedVault,
      });
      dispatchVaultDetail({
        type: vaultDetailActions.SET_INITIAL_ALBUMS,
        albums: [
          ...(fetchedVault.albums || []),
          ...(fetchedVault.unansweredAlbums || []),
        ],
      });
      setAlbum(fetchedAlbum);
    } catch (error) {
      goToPage(RouteName.NotFoundRedirect);
    } finally {
      if (!silent) setFetching(false);
    }
  };

  useEffect(() => {
    if (vaultId > 0 && albumId > 0) fetchAlbum();
  }, [vaultId, albumId]);

  // Something outside the album (e.g. the top bar's "New Prompt") changed it.
  useEffect(() => {
    if (refreshToken > 0 && vaultId > 0 && albumId > 0) fetchAlbum(true);
  }, [refreshToken]);

  // Deep link from the top-bar "New" menu: ?add=<type> opens the Add Entry
  // modal directly. write/audio/video jump into the creator; upload (document)
  // lands on the picker, where the Upload option lives.
  useEffect(() => {
    const addParam = new URLSearchParams(location.search).get('add');
    if (!addParam) return;
    const preselectable: AlbumAddContentTypes[] = [
      AlbumAddContentTypes.write,
      AlbumAddContentTypes.audio,
      AlbumAddContentTypes.video,
    ];
    if (preselectable.includes(addParam as AlbumAddContentTypes)) {
      setAddEntryInitialType(addParam as AlbumAddContentTypes);
      setShowAddEntryModal(true);
    } else if (addParam === AlbumAddContentTypes.upload) {
      setShowUploadModal(true);
    }
  }, [location.search]);

  const handleChangeView = (nextView: AlbumViewKind) => {
    if (nextView === view || viewPhase === 'leaving') return;
    setView(nextView);
    setViewPhase('leaving');
    window.setTimeout(() => {
      setDisplayedView(nextView);
      setViewPhase('entering');
      window.setTimeout(() => setViewPhase('idle'), VIEW_ENTER_MS);
    }, VIEW_LEAVE_MS);
  };

  // The player keeps the same trail as this page, so forward the origin.
  const goToViewMode = (contentId?: number) =>
    goToPage(
      RouteName.AlbumViewScreen,
      { id: vaultId, albumId },
      contentId ? { content: contentId } : {},
      withOrigin(breadcrumbOrigin),
    );

  const visibleContents = useMemo(() => {
    const contents = album?.contents ?? [];
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? contents.filter((c) => c.name?.toLowerCase().includes(query))
      : contents;
    return sortAlbumContents(filtered, sortBy);
  }, [album?.contents, searchQuery, sortBy]);

  const promptContents = useMemo(
    () => visibleContents.filter(isPromptContent),
    [visibleContents],
  );
  const answeredContents = useMemo(
    () => visibleContents.filter((c) => !isPromptContent(c)),
    [visibleContents],
  );

  const handleAlbumUpdated = (partial: Partial<AlbumDetailType>) =>
    setAlbum((prev) => (prev ? { ...prev, ...partial } : prev));

  const handleContentUpdated = (
    contentId: number,
    partial: Partial<AlbumContentSerialized>,
  ) =>
    setAlbum((prev) =>
      prev
        ? {
            ...prev,
            contents: prev.contents.map((c) =>
              c.id === contentId ? { ...c, ...partial } : c,
            ),
          }
        : prev,
    );

  // Deleted, or moved out to Unorganized Memories: either way it leaves the grid.
  const handleContentRemoved = (contentId: number) =>
    setAlbum((prev) =>
      prev
        ? { ...prev, contents: prev.contents.filter((c) => c.id !== contentId) }
        : prev,
    );

  const handleAlbumDeleted = () =>
    goToPage(
      RouteName.VaultDetail,
      { id: vaultId },
      { tab: VaultNavigationTabs.albums },
    );

  if (fetching || !album || !vault) return <Spinner />;

  const { uploads } = selfRecordingState;

  const breadcrumb = albumPageBreadcrumb({
    vaultId,
    albumName: album.name,
    origin: breadcrumbOrigin,
  });
  // Phones go back to the crumb before the album: where it was opened from.
  const goBack =
    breadcrumb[breadcrumb.length - 2]?.onClick ??
    (() =>
      goToPage(
        RouteName.VaultDetail,
        { id: vaultId },
        { tab: VaultNavigationTabs.albums },
      ));

  const navRow = (
    <AlbumNavRow
      vaultId={vaultId}
      view={view}
      onChangeView={handleChangeView}
      sortBy={sortBy}
      onChangeSort={setSortBy}
      showCustomOrder={hasAlbumOrder(album?.contents ?? [])}
      phone={isPhone}
    />
  );

  // The vault's hidden default album, where "Remove from album" moves a
  // memory (Unorganized Memories). Not offered on that album itself.
  const unsortedAlbumId = album.isDefault
    ? undefined
    : [...(vault.albums ?? []), ...(vault.unansweredAlbums ?? [])].find(
        (vaultAlbum) => vaultAlbum.isDefault,
      )?.id;

  return (
    <div className={styles.page}>
      {/* Phones: the mobile app's album screen. Its own header (back,
          search, sort), the hero, then the Grid / Planner tabs. */}
      {isPhone ? (
        <AlbumPhoneHeader
          title={album.name}
          onBack={goBack}
          sortBy={sortBy}
          onChangeSort={setSortBy}
          showCustomOrder={hasAlbumOrder(album?.contents ?? [])}
        />
      ) : (
        <VaultTopBar
          vault={vault}
          uploadAlbum={{
            id: album.id,
            name: album.name,
            thumbnailUrl: album.thumbnailUrl,
          }}
          breadcrumb={breadcrumb}
          searchChip={{
            label: album.name,
            thumbnailContent: album.contents.find((content) =>
              [AlbumContentType.image, AlbumContentType.video].includes(
                adaptContentType(content),
              ),
            ),
            // Leaving the album's search scope returns to the vault dashboard.
            onRemove: () => goToPage(RouteName.VaultDetail, { id: vaultId }),
          }}
        />
      )}
      <div className={styles.canvas}>
        {!isPhone && navRow}
        <AlbumHeader
          album={album}
          vault={vault}
          onAddEntry={(mode) => {
            setAddEntryInitialType(mode ?? null);
            setShowAddEntryModal(true);
          }}
          onUpload={() => setShowUploadModal(true)}
          onView={() => goToViewMode()}
          onAlbumUpdated={handleAlbumUpdated}
          onAlbumDeleted={handleAlbumDeleted}
          onContentsChanged={() => fetchAlbum(true)}
        />
        {isPhone && navRow}

        <div
          className={classnames(styles.viewStage, {
            [styles.viewLeaving]: viewPhase === 'leaving',
            [styles.viewEntering]: viewPhase === 'entering',
          })}
        >
          {displayedView === 'grid' ? (
            <MemoryGrid
              contents={answeredContents}
              onSelect={(content) => goToViewMode(content.id)}
              uploads={uploads}
              uploadsAtStart={sortBy === 'newest' || sortBy === 'recentlyAdded'}
              onUploadSuccess={() => fetchAlbum(true)}
              onContentUpdated={handleContentUpdated}
              onContentRemoved={handleContentRemoved}
              unsortedAlbumId={unsortedAlbumId}
            />
          ) : (
            <AlbumPlanner
              vault={vault}
              albumId={albumId}
              albumName={albumDisplayName(album)}
              prompts={promptContents}
              answered={answeredContents}
              onAddEntry={() => setShowAddEntryModal(true)}
              onViewMode={goToViewMode}
              onContentsChanged={() => fetchAlbum(true)}
            />
          )}
        </div>
      </div>

      {showUploadModal && vault && (
        <UploadModal
          vaultId={vault.id}
          album={{
            id: album.id,
            name: album.name,
            thumbnailUrl: album.thumbnailUrl,
          }}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {showAddEntryModal && (
        <NewEntryModal
          mode={addEntryInitialType ?? AlbumAddContentTypes.write}
          vault={vault}
          albums={[{ id: album.id, name: album.name }]}
          defaultAlbumId={album.id}
          onClose={() => {
            setShowAddEntryModal(false);
            setAddEntryInitialType(null);
            // Refresh so the new entry (and any date/location edits) show up.
            fetchAlbum(true);
          }}
        />
      )}
    </div>
  );
};

// New owner-facing single-album page (/vaults/:id/album/:albumId) with the
// Grid / Planner views. The classic fullscreen player lives one level deeper
// at /vaults/:id/album/:albumId/view (RouteName.AlbumViewScreen).
const SingleAlbumInner = () => (
  <VaultDetailProvider>
    <SingleAlbumContent />
  </VaultDetailProvider>
);

const SingleAlbum = withAuth(SingleAlbumInner);

export { SingleAlbum };
